import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import apis from '../../utils/apis';
import Spinner from '../shared/TailwindComponents/Spinner';
import logger from '../../utils/logger';
import WizardToggleRow from './WizardToggleRow';

interface SearchIndex {
  index_name: string;
  display_name: string;
  category: string;
  source_type: string;
  index_exists?: boolean;
  document_count?: number | null;
}

interface IndexSourceSelectorProps {
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  jurisdiction?: string;
}

// Group headings are plain text: no section heading in the wizard carries an
// icon, and half of these had none anyway, so the list read as a mix.
const CATEGORY_LABELS: Record<string, string> = {
  legislation: 'Legislation',
  case_law: 'Case Law',
  preparatory_works: 'Preparatory Works',
  regulation: 'Regulations',
  agency_decisions: 'Agency Decisions',
  gazette: 'Gazette',
  // Bulgaria publishes parliamentary material as its own category. Without an
  // entry the raw backend string was shown, untranslated, as the heading.
  parliamentary: 'Parliamentary',
  company_filings: 'Company Filings',
  legal_sources: 'Legal Sources',
  other: 'Other',
};

/** An unmapped backend category, readable: "LEGAL_SOURCES" → "Legal sources". */
const humanizeCategory = (raw: string): string => {
  const words = raw.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const IndexSourceSelector: React.FC<IndexSourceSelectorProps> = ({
  value,
  onChange,
  disabled,
  jurisdiction,
}) => {
  const { t } = useTranslation();
  const [availableIndexes, setAvailableIndexes] = useState<SearchIndex[]>([]);
  const [loading, setLoading] = useState(false);
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);
  onChangeRef.current = onChange;
  valueRef.current = value;

  const fetchIndexes = useCallback(async (country: string) => {
    if (!country) return;
    setLoading(true);
    try {
      const response = await apis({
        method: 'GET',
        url: '/matters/available-indexes',
        params: { country },
      });
      const indexes: SearchIndex[] = response?.data?.indexes || [];
      setAvailableIndexes(indexes);
      // Default-select the new jurisdiction's sources. Old selections are from a
      // different country and won't match. Prefer sources whose index is
      // actually deployed; if none report that flag, fall back to all.
      if (indexes.length > 0) {
        const deployed = indexes.filter((idx) => idx.index_exists !== false);
        const toSelect = (deployed.length > 0 ? deployed : indexes).map((idx) => idx.index_name);
        onChangeRef.current(toSelect);
      }
    } catch (error) {
      logger.warn('[IndexSourceSelector] Failed to fetch indexes', { error });
      setAvailableIndexes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (jurisdiction) {
      fetchIndexes(jurisdiction);
    } else {
      setAvailableIndexes([]);
    }
  }, [jurisdiction, fetchIndexes]);

  // Say why the list is empty instead of rendering nothing under a heading.
  if (availableIndexes.length === 0 && !loading) {
    return (
      <p className="text-sm text-fg-muted">
        {jurisdiction
          ? t('No legal sources are available for this jurisdiction yet.')
          : t('Set a jurisdiction to see its legal sources.')}
      </p>
    );
  }

  const selected = new Set(value || []);

  const toggleIndex = (indexName: string) => {
    const next = new Set(selected);
    if (next.has(indexName)) {
      next.delete(indexName);
    } else {
      next.add(indexName);
    }
    onChange(Array.from(next));
  };

  const toggleAll = () => {
    if (selected.size === availableIndexes.length) {
      onChange([]);
    } else {
      onChange(availableIndexes.map((idx) => idx.index_name));
    }
  };

  // Group by category
  const grouped = availableIndexes.reduce<Record<string, SearchIndex[]>>((acc, idx) => {
    const cat = idx.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(idx);
    return acc;
  }, {});

  const allSelected = selected.size === availableIndexes.length;

  return (
    <div className="space-y-4">
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-fg-muted">
          <Spinner size="glyph" />
          {t('Loading available sources...')}
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <p className="text-xs text-fg-muted">
              {selected.size} / {availableIndexes.length} {t('sources selected')}
            </p>
            <button
              type="button"
              onClick={toggleAll}
              disabled={disabled}
              className="text-xs font-medium text-fg-body hover:text-fg-strong disabled:opacity-50"
            >
              {allSelected ? t('Deselect all') : t('Select all')}
            </button>
          </div>

          {Object.entries(grouped).map(([category, indexes]) => {
            const label = CATEGORY_LABELS[category.toLowerCase()];
            return (
              <div key={category}>
                <p className="text-xs font-medium text-fg-muted mb-2">
                  {label ? t(label) : humanizeCategory(category)}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {indexes.map((idx) => (
                    <WizardToggleRow
                      key={idx.index_name}
                      label={idx.display_name}
                      meta={typeof idx.document_count === 'number'
                        ? `${idx.document_count.toLocaleString()} ${t('docs')}`
                        : undefined}
                      enabled={selected.has(idx.index_name)}
                      onToggle={() => toggleIndex(idx.index_name)}
                      disabled={disabled}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default IndexSourceSelector;
